import { type SecurityAlert } from '@/mocks/saSecurity';
import { ALERT_SEVERITY_LABELS } from './securityLabels';

interface Props {
  alerts: SecurityAlert[];
  onAcknowledge: (id: string) => void;
  onViewEvent: (eventId: string) => void;
}

export default function SecurityAlertsPanel({ alerts, onAcknowledge, onViewEvent }: Props) {
  const safeAlerts = Array.isArray(alerts) ? alerts : [];

  const unacknowledged = safeAlerts.filter((a) => !a.acknowledged);
  const acknowledged = safeAlerts.filter((a) => a.acknowledged);

  if (safeAlerts.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-6 text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-emerald-50 flex items-center justify-center">
          <i className="ri-shield-check-line text-emerald-400 text-xl"></i>
        </div>
        <p className="text-foreground-800 text-xs font-semibold mb-1">Sin alertas activas</p>
        <p className="text-foreground-400 text-[10px]">El sistema se encuentra en estado normal.</p>
      </div>
    );
  }

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-background-200 bg-background-100/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center">
            <i className="ri-alert-fill text-red-500 text-sm"></i>
          </div>
          <div>
            <p className="text-foreground-950 text-xs font-bold">Alertas de Seguridad</p>
            <p className="text-foreground-400 text-[10px]">{unacknowledged.length} pendientes · {acknowledged.length} revisadas</p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-background-200">
        {safeAlerts.map((alert) => {
          const sevMeta = ALERT_SEVERITY_LABELS[alert.severity] || { label: alert.severity, color: 'bg-background-100 text-foreground-500' };

          return (
            <div key={alert.id} className={`p-4 ${alert.acknowledged ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-start gap-3 min-w-0">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[9px] font-bold uppercase shrink-0 ${sevMeta.color}`}>
                    {sevMeta.label}
                  </span>
                  <div className="min-w-0">
                    <p className="text-foreground-800 text-xs font-semibold">{alert.title}</p>
                    <p className="text-foreground-400 text-[10px] mt-0.5 line-clamp-2">{alert.description}</p>
                  </div>
                </div>
                <span className="text-foreground-400 text-[10px] whitespace-nowrap shrink-0">{alert.timestamp}</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-foreground-400 text-[10px] bg-background-100 px-1.5 py-0.5 rounded capitalize">{alert.source}</span>
                <span className="text-foreground-400 text-[10px] bg-background-100 px-1.5 py-0.5 rounded capitalize">{alert.category}</span>
                {!alert.acknowledged && (
                  <button
                    onClick={() => onAcknowledge(alert.id)}
                    className="text-emerald-600 text-[10px] font-medium cursor-pointer hover:text-emerald-700 flex items-center gap-1 ml-auto"
                  >
                    <i className="ri-check-line text-[9px]"></i>
                    Reconocer
                  </button>
                )}
                <button
                  onClick={() => onViewEvent(alert.relatedEventId)}
                  className="text-foreground-500 text-[10px] font-medium cursor-pointer hover:text-foreground-700 flex items-center gap-1"
                >
                  <i className="ri-external-link-line text-[9px]"></i>
                  Ver evento
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}